'use client';

import clsx from 'clsx';
import Link, { LinkProps } from 'next/link';
import { ButtonHTMLAttributes, FC, SVGProps } from 'react';
import IButton from '~/data/strapi/types/shared-components/button';
import { useMetrika } from '~/providers/ya-metrica';
import getDataPath from '~/utils/get-data-path';

import Icon from '../icon';
import Loader from '../loading';
import ButtonIcon from './enum/button-icon';
import ButtonVariant from './enum/button-variant';
import styles from './styles.module.css';

const BUTTON_STYLE: Record<string, string> = {
  [ButtonVariant.PRIMARY]: styles.blue,
  [ButtonVariant.SECONDARY]: styles.ghost,
};

const BUTTON_ICON: Record<string, FC<SVGProps<SVGElement>>> = {
  [ButtonIcon.OPEN]: ({ className }) => (
    <Icon className={className} name="open" strokeWidth={2} strokeLinecap="square" strokeLinejoin="round" />
  ),
};

interface Props {
  data?: IButton;
  className?: string;
  title?: string;
  icon?: ButtonIcon;
  variant?: ButtonVariant;
  children?: React.ReactNode;
  onClick?: () => void;
  href?: string;
  linkProps?: Omit<LinkProps, 'href'>;
  buttonProps?: Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'>;
  loading?: boolean;
}

const Button: FC<Props> = ({
  data,
  className,
  title,
  icon,
  variant = ButtonVariant.PRIMARY,
  children,
  onClick,
  href,
  linkProps,
  buttonProps,
  loading,
}) => {
  const buttonTitle = data?.title || title || children;
  const downloadMedia = data?.downloadMedia;
  const downloadHref = downloadMedia ? getDataPath(downloadMedia?.url) : null;
  const buttonHref = data?.href || downloadHref || href;
  const buttonVariant = data?.variant || variant;
  const buttonIcon = data?.icon || icon;

  const buttonStyle = BUTTON_STYLE[buttonVariant];
  const Icon = buttonIcon && BUTTON_ICON[buttonIcon];
  const { captureGoal } = useMetrika();

  if (!buttonTitle) return null;

  const ymHandler = () => {
    if (data?.yaMetricaEventUID) captureGoal(data.yaMetricaEventUID);
  };

  if (buttonHref) {
    return (
      <Link
        onClick={ymHandler}
        href={buttonHref}
        className={clsx(styles.button, buttonStyle, className)}
        target={downloadHref ? '_blank' : undefined}
        {...linkProps}
      >
        {Icon && <Icon className={clsx(variant === ButtonVariant.PRIMARY ? styles.iconWhite : styles.icon)} />}
        {buttonTitle}
      </Link>
    );
  }

  const clickHandler = () => {
    onClick?.();
    ymHandler();
  };

  return (
    <button
      {...buttonProps}
      onClick={clickHandler}
      className={clsx(styles.button, buttonStyle, className, loading && styles.loading)}
    >
      <span className={styles.btnChildren}>
        {Icon && <Icon />}
        {buttonTitle}
      </span>
      {loading && <Loader containerClassName={styles.loader} />}
    </button>
  );
};

export default Button;
